import React from 'react'
import { Button, Checkbox, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import moment from 'moment'
import { sellerEmployeesApi } from 'api/sellerEmployees'
import {
  ALL_SELLER_PERMISSIONS,
  ESellerEmployeeStatus,
  ISellerEmployee,
  ISellerOwner,
  SELLER_PERMISSION_LABELS
} from 'models/sellerEmployees'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { showServerError } from 'shared/utils/showServerError'
import { MainTitle } from 'components/ui'
import { EmployeeStatusTag } from 'components/employees/EmployeeStatusTag/EmployeeStatusTag'
import { SellerEmployeeModal } from './SellerEmployeeModal'
import styles from './SellerEmployees.module.scss'

interface ISellerEmployeesProps {
  // карточка продавца у супер-админа; в своём кабинете магазин берётся из токена
  sellerId?: string
}

interface IEmployeeRow {
  _id: string
  name: string
  surname: string
  email: string
  createdAt: string | null
  // null — строка владельца магазина
  employee: ISellerEmployee | null
}

const OWNER_ROW_ID = 'owner'

const getPermissionsText = ({ employee }: IEmployeeRow) => {
  if (!employee || employee.permissions.length === ALL_SELLER_PERMISSIONS.length)
    return 'Все разделы'
  if (!employee.permissions.length) return 'Только основная информация'
  return employee.permissions
    .map((permission) => SELLER_PERMISSION_LABELS[permission])
    .join(', ')
}

const formatDate = (date: string | null) =>
  date ? moment(date).format('DD.MM.YYYY') : '—'

const getFullName = ({ name, surname }: IEmployeeRow) =>
  `${name} ${surname}`.trim()

const toEmployeeRow = (employee: ISellerEmployee): IEmployeeRow => ({
  _id: employee._id,
  name: employee.name,
  surname: employee.surname,
  email: employee.email,
  createdAt: employee.createdAt,
  employee
})

const toOwnerRow = (owner: ISellerOwner): IEmployeeRow => ({
  _id: OWNER_ROW_ID,
  name: owner.name,
  surname: owner.surname,
  email: owner.email,
  createdAt: owner.since,
  employee: null
})

export const SellerEmployees: React.FC<ISellerEmployeesProps> = ({
  sellerId
}) => {
  const isMobile = useIsMobile()
  // раздел в своём кабинете открыт только владельцу, сотрудникам он закрыт
  const isOwnCabinet = !sellerId
  const [owner, setOwner] = React.useState<ISellerOwner | null>(null)
  const [employees, setEmployees] = React.useState<ISellerEmployee[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [showFired, setShowFired] = React.useState(false)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [editedEmployee, setEditedEmployee] =
    React.useState<ISellerEmployee | null>(null)

  React.useEffect(() => {
    sellerEmployeesApi
      .getOwner(sellerId)
      .then(({ data }) => setOwner(data))
      // без строки владельца список сотрудников всё равно рабочий
      .catch(() => setOwner(null))
  }, [sellerId])

  React.useEffect(() => {
    const getEmployees = async () => {
      try {
        setIsLoading(true)
        const { data } = await sellerEmployeesApi.getEmployees({
          include_fired: showFired || undefined,
          seller_id: sellerId
        })
        setEmployees(data)
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoading(false)
      }
    }
    getEmployees()
  }, [sellerId, showFired])

  const rows = React.useMemo(
    () => [
      ...(owner ? [toOwnerRow(owner)] : []),
      ...employees.map(toEmployeeRow)
    ],
    [owner, employees]
  )

  const openModal = (employee: ISellerEmployee | null) => {
    setEditedEmployee(employee)
    setIsModalOpen(true)
  }

  const onSaved = (saved: ISellerEmployee) => {
    setEmployees((prev) =>
      prev.some((item) => item._id === saved._id)
        ? prev.map((item) => (item._id === saved._id ? saved : item))
        : [saved, ...prev]
    )
    setIsModalOpen(false)
  }

  const onFired = (fired: ISellerEmployee) => {
    setEmployees((prev) =>
      showFired
        ? prev.map((item) => (item._id === fired._id ? fired : item))
        : prev.filter((item) => item._id !== fired._id)
    )
    setIsModalOpen(false)
  }

  const onRevoked = (id: string) => {
    setEmployees((prev) => prev.filter((item) => item._id !== id))
    setIsModalOpen(false)
  }

  const renderName = (record: IEmployeeRow) => (
    <>
      {getFullName(record) || record.email}
      {!record.employee && isOwnCabinet && (
        <span className={styles.youMark}> (вы)</span>
      )}
    </>
  )

  const renderRole = ({ employee }: IEmployeeRow) =>
    employee ? 'Сотрудник' : 'Владелец'

  const renderStatus = ({ employee }: IEmployeeRow) =>
    employee ? (
      <EmployeeStatusTag {...employee} />
    ) : (
      <EmployeeStatusTag status={ESellerEmployeeStatus.ACTIVE} />
    )

  const columns: ColumnsType<IEmployeeRow> = [
    {
      title: 'Сотрудник',
      dataIndex: 'name',
      width: 240,
      render: (_, record) => (
        <div className={styles.employeeCell}>
          <span>{renderName(record)}</span>
          {getFullName(record) && (
            <span className={styles.email}>{record.email}</span>
          )}
        </div>
      )
    },
    {
      title: 'Доступ',
      dataIndex: 'permissions',
      render: (_, record) => (
        <Typography.Paragraph
          style={{ marginBottom: 0 }}
          ellipsis={{ rows: 2, tooltip: getPermissionsText(record) }}
        >
          {getPermissionsText(record)}
        </Typography.Paragraph>
      )
    },
    {
      title: 'Добавлен',
      dataIndex: 'createdAt',
      width: 112,
      render: (_, { createdAt }) => formatDate(createdAt)
    },
    {
      title: 'Роль',
      dataIndex: 'role',
      width: 112,
      render: (_, record) => renderRole(record)
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: 132,
      render: (_, record) => renderStatus(record)
    }
  ]

  const mobileColumns: ColumnsType<IEmployeeRow> = [
    {
      title: 'Сотрудник',
      dataIndex: 'name',
      width: '65%',
      render: (_, record) => (
        <div className={styles.mobileCell}>
          <span className={styles.mobileName}>{renderName(record)}</span>
          {getFullName(record) && (
            <span className={styles.mobileSubText}>{record.email}</span>
          )}
          <span className={styles.mobileSubText}>
            {getPermissionsText(record)}
          </span>
        </div>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: '35%',
      render: (_, record) => (
        <div className={styles.mobileStatusCell}>
          <span className={styles.mobileSubText}>{renderRole(record)}</span>
          {renderStatus(record)}
        </div>
      )
    }
  ]

  const isRowClickable = ({ employee }: IEmployeeRow) =>
    Boolean(employee) && employee?.status !== ESellerEmployeeStatus.FIRED

  return (
    <div>
      <div className={styles.header}>
        <MainTitle text='Сотрудники' style={{ marginBottom: 0 }} hideOnMobile />
        <div className={styles.headerControls}>
          <Checkbox
            checked={showFired}
            onChange={(e) => setShowFired(e.target.checked)}
          >
            Показать уволенных
          </Checkbox>
          <Button
            type='primary'
            size='large'
            icon={<PlusOutlined />}
            onClick={() => openModal(null)}
          >
            Пригласить
          </Button>
        </div>
      </div>
      <Table
        scroll={isMobile ? undefined : { x: 760 }}
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        columns={isMobile ? mobileColumns : columns}
        dataSource={rows}
        loading={isLoading}
        rowKey={(el) => el._id}
        rowClassName={(record) => {
          if (record.employee?.status === ESellerEmployeeStatus.FIRED)
            return styles.firedRow
          return isRowClickable(record) ? 'tableRow' : ''
        }}
        onRow={(record) => ({
          onClick: () =>
            isRowClickable(record) && openModal(record.employee)
        })}
        pagination={false}
        locale={{ emptyText: 'Сотрудников пока нет' }}
      />
      <SellerEmployeeModal
        open={isModalOpen}
        employee={editedEmployee}
        sellerId={sellerId}
        canTransferOwnership={isOwnCabinet}
        onClose={() => setIsModalOpen(false)}
        onSaved={onSaved}
        onFired={onFired}
        onRevoked={onRevoked}
      />
    </div>
  )
}
